import type { RequestInit, Response } from 'node-fetch';
import { IFetchHelper } from '@/interfaces/helper/IFetchHelper';
import { FetchHelper } from '@/utils/helper/FetchHelper';
import { logger } from '@/logger';

const localLogger = logger.child({ module: 'RetryingFetchHelper' });

const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_FALLBACK_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

/**
 * An {@link IFetchHelper} decorator that transparently retries Nexudus rate-limit
 * (HTTP 429) responses, honouring the `Retry-After` header when present.
 *
 * Nexudus gets aggressive about throttling, and the main {@link NexudusService}
 * fans many small writes out per request (e.g. one POST per form-page answer),
 * which is enough to trip the limit. Putting the retry here — at the single
 * `IFetchHelper` chokepoint every service method shares — means all Nexudus
 * calls benefit, including the methods that bypass NexudusResponseChecker and
 * check `response.ok` themselves.
 *
 * Only 429 is retried. A 429 means the request was rejected *before* being
 * processed, so retrying a non-idempotent POST is safe and creates no duplicate
 * writes. Every other response (including 5xx, which may have had side-effects)
 * is returned untouched, so the caller still owns the body — we only read the
 * status and headers here, which does not consume the body stream.
 */
export class RetryingFetchHelper implements IFetchHelper {
  constructor(
    private readonly delegate: IFetchHelper = new FetchHelper(),
    private readonly maxRetries: number = DEFAULT_MAX_RETRIES,
    private readonly fallbackBackoffMs: number = DEFAULT_FALLBACK_BACKOFF_MS,
    private readonly random: () => number = Math.random,
  ) {}

  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    let attempt = 0;

    for (;;) {
      const response = await this.delegate.fetch(url, options);

      if (response.status !== 429 || attempt >= this.maxRetries) {
        return response;
      }

      const waitMs = this.backoffMs(response, attempt);
      attempt += 1;
      localLogger.warn(
        { url, method: options.method ?? 'GET', attempt, maxRetries: this.maxRetries, waitMs },
        'Nexudus returned 429, backing off before retry',
      );
      await new Promise(resolve => setTimeout(resolve, waitMs));
    }
  }

  /**
   * Prefer the server's `Retry-After` (seconds). Fall back to exponential
   * backoff when it's missing, and cap either way so a misbehaving header can't
   * stall a request indefinitely.
   *
   * Jitter is added on top of the base wait. Nexudus sends a fixed
   * `Retry-After` (often 1s), so without jitter a fan-out of throttled requests
   * all sleep the same amount, wake together and re-trip the limit in lockstep
   * (a thundering herd). Spreading each retry over [base, 2*base] breaks that up.
   */
  private backoffMs(response: Response, attempt: number): number {
    const retryAfterSeconds = Number.parseInt(response.headers.get('Retry-After') ?? '', 10);
    const base =
      Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
        ? Math.min(retryAfterSeconds * 1000, MAX_BACKOFF_MS)
        : Math.min(this.fallbackBackoffMs * 2 ** attempt, MAX_BACKOFF_MS);

    return Math.min(base + this.random() * base, MAX_BACKOFF_MS);
  }
}
